import React from "react"
import { Link } from "react-router-dom"

// Fake assignment data for demo screenshots
const demoAssignments = [
  {
    id: 1,
    name: "Chapter 8 Reading Quiz",
    course: "Biology 101",
    dueIn: "in 3h",
    points: 25,
    status: "pending",
    urgency: "today",
    courseColor: "bg-violet-500",
  },
  {
    id: 2,
    name: "Research Paper Draft",
    course: "English Composition",
    dueIn: "Tomorrow",
    points: 100,
    status: "pending",
    urgency: "tomorrow",
    courseColor: "bg-cyan-500",
  },
  {
    id: 3,
    name: "Problem Set #6",
    course: "Calculus II",
    dueIn: "2 days",
    points: 50,
    grade: { score: 47, possible: 50, percentage: 94 },
    status: "graded",
    urgency: "completed",
    courseColor: "bg-fuchsia-500",
  },
  {
    id: 4,
    name: "Lab Report: Chemical Reactions",
    course: "Chemistry",
    dueIn: "3d overdue",
    points: 75,
    status: "missing",
    urgency: "overdue",
    courseColor: "bg-orange-500",
  },
]

// Demo stats for the stats bar preview
const demoStats = {
  overdue: 2,
  today: 3,
  thisWeek: 8,
  submitted: 12,
}

// Fake grade circle component
const GradeCircle = ({ percentage }) => {
  const radius = 14
  const circumference = 2 * Math.PI * radius
  const progress = (percentage / 100) * circumference
  const colorClass = percentage >= 90 ? "text-green-600" : percentage >= 80 ? "text-blue-600" : "text-yellow-600"

  return (
    <div className="relative w-9 h-9">
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
        <circle cx="18" cy="18" r={radius} fill="none" stroke="currentColor" strokeWidth="3" className="text-gray-200" />
        <circle
          cx="18"
          cy="18"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
          className={colorClass}
        />
      </svg>
      <span className={`absolute inset-0 flex items-center justify-center text-xs font-medium ${colorClass}`}>
        {Math.round(percentage)}
      </span>
    </div>
  )
}

// Demo Assignment Card
const DemoAssignmentCard = ({ assignment }) => {
  const urgencyColors = {
    overdue: "#c23b22",
    today: "#d4820a",
    tomorrow: "#2563eb",
    completed: "#10b981",
  }

  return (
    <div
      className="assignment-card block shadow-sm p-4 bg-white"
      style={{ "--urgency-color": urgencyColors[assignment.urgency] || "#ebe6dd" }}
    >
      <div className="flex items-start gap-3">
        <button
          className={`
            mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 transition-all duration-200
            flex items-center justify-center
            ${assignment.status === "graded" ? "bg-emerald-500 border-emerald-500 text-white" : "border-[var(--cream)]"}
          `}
        >
          {assignment.status === "graded" && (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>

        <div className={`w-1 h-12 rounded-full ${assignment.courseColor} flex-shrink-0 opacity-60`} />

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3
                className={`
                font-medium text-[var(--ink)] leading-snug
                ${assignment.status === "graded" ? "text-[var(--ink-light)]" : ""}
              `}
              >
                {assignment.name}
              </h3>
              <p className="text-sm text-[var(--ink-light)] mt-0.5 truncate">{assignment.course}</p>
            </div>

            <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
              {assignment.status === "graded" && (
                <span className="badge bg-emerald-100 text-emerald-700">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                    />
                  </svg>
                  Graded
                </span>
              )}
              {assignment.status === "missing" && (
                <span className="badge bg-red-100 text-red-700">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  Missing
                </span>
              )}
              {assignment.grade && (
                <div className="flex items-center gap-2">
                  <GradeCircle percentage={assignment.grade.percentage} />
                  <span className="text-sm font-medium text-green-600">
                    {assignment.grade.score}/{assignment.grade.possible}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-2.5 flex items-center gap-3 text-sm">
            <span
              className={`flex items-center gap-1.5 ${
                assignment.urgency === "overdue"
                  ? "text-[var(--urgent)] font-medium"
                  : assignment.urgency === "today"
                    ? "text-[var(--warning)] font-medium"
                    : "text-[var(--ink-light)]"
              }`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {assignment.dueIn}
            </span>
            <span className="text-[var(--ink-light)] flex items-center gap-1">
              <svg className="w-3.5 h-3.5 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {assignment.points} pts
            </span>
            {assignment.status === "missing" && <span className="badge badge-missing">Missing</span>}
          </div>
        </div>
      </div>
    </div>
  )
}

// Demo Stats Card
const DemoStatCard = ({ label, value, gradient, iconColor, urgent, icon }) => {
  return (
    <div className="stat-card relative p-4 text-left shadow-sm">
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${gradient} rounded-t-2xl`} />
      <div className="pt-1">
        <div className="flex items-center justify-between mb-1">
          <span className={iconColor}>{icon}</span>
          <span
            className={`font-display text-3xl font-bold tracking-tight ${value > 0 ? iconColor.replace("text-", "text-") : "text-[var(--ink-light)]"} ${urgent && value > 0 ? "pulse-urgent" : ""}`}
          >
            {value}
          </span>
        </div>
        <p className="text-sm font-medium text-[var(--ink-muted)]">{label}</p>
      </div>
    </div>
  )
}

const MarketingPage = () => {
  return (
    <div className="min-h-screen bg-[var(--paper)]">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-[var(--paper)]/95 backdrop-blur-sm border-b border-[var(--cream)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-3 min-h-16 py-3">
            <div className="flex items-center">
              <img
                src="/images/heartilylogo.png"
                alt="Heartily"
                className="h-8"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Link to="/ai-assistants" className="px-2 py-2 text-sm font-medium text-[var(--ink-muted)] hover:text-[var(--ink)]">AI assistants</Link>
              <a
                href="/users/sign_in"
                className="px-4 py-2 text-sm font-medium text-[var(--ink-muted)] hover:text-[var(--ink)] transition-colors"
              >
                Sign In
              </a>
              <a
                href="/users/sign_up"
                className="px-5 py-2.5 text-sm font-medium bg-[var(--ink)] text-white rounded-xl hover:bg-[var(--ink-muted)] transition-colors shadow-sm"
              >
                Get Started Free
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--cream)]/30 to-transparent pointer-events-none" />

        {/* Floating decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating assignment card snippet - left */}
          <div
            className="absolute top-32 left-[5%] w-48 h-16 bg-white rounded-xl shadow-lg opacity-60 float hidden lg:block"
            style={{ "--rotate": "-6deg" }}
          >
            <div className="p-3 flex items-center gap-3">
              <div className="w-4 h-4 rounded-full border-2 border-amber-400" />
              <div className="flex-1">
                <div className="h-2.5 bg-[var(--cream)] rounded w-24 mb-1.5" />
                <div className="h-2 bg-[var(--cream)] rounded w-16 opacity-60" />
              </div>
            </div>
          </div>

          {/* Floating assignment card snippet - right */}
          <div
            className="absolute top-48 right-[8%] w-44 h-14 bg-white rounded-xl shadow-lg opacity-50 float-delayed hidden lg:block"
            style={{ "--rotate": "4deg" }}
          >
            <div className="p-3 flex items-center gap-3">
              <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="h-2.5 bg-[var(--cream)] rounded w-20 mb-1.5" />
                <div className="h-2 bg-emerald-100 rounded w-12" />
              </div>
            </div>
          </div>

          {/* Floating stat badge - left lower */}
          <div
            className="absolute top-72 left-[12%] float-slow hidden lg:block"
            style={{ "--rotate": "-3deg" }}
          >
            <div className="px-3 py-2 bg-white rounded-lg shadow-md opacity-40">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-gradient-to-br from-amber-500 to-orange-500" />
                <span className="text-xs font-semibold text-[var(--ink-muted)]">3 Today</span>
              </div>
            </div>
          </div>

          {/* Floating checkmark icon - right lower */}
          <div
            className="absolute bottom-40 right-[15%] float hidden lg:block"
            style={{ "--rotate": "8deg" }}
          >
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center opacity-50 shadow-sm">
              <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>

          {/* Floating clock icon - left */}
          <div
            className="absolute bottom-52 left-[8%] float-delayed hidden lg:block"
            style={{ "--rotate": "-5deg" }}
          >
            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center opacity-40 shadow-sm">
              <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8 sm:pt-24 sm:pb-12 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            {/* Free badge */}
            <div className="hero-animate hero-animate-delay-1 inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium mb-8 ring-1 ring-emerald-200 badge-animated">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Free for Spring 2026
            </div>

            <h1 className="hero-animate hero-animate-delay-2 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[var(--ink)] leading-tight mb-6">
              Never miss another
              <br />
              <span className="text-shimmer">
                assignment
              </span>
            </h1>

            <p className="hero-animate hero-animate-delay-3 text-lg sm:text-xl text-[var(--ink-muted)] mb-10 leading-relaxed max-w-2xl mx-auto">
              Heartily syncs with your LMS to organize all your assignments by urgency. See what's overdue,
              due today, and coming up—all in one beautiful dashboard. Built for students who want to do their work
              with excellence.
            </p>

            <div className="hero-animate hero-animate-delay-4 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/users/sign_up"
                className="btn-glow inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold bg-[var(--ink)] text-white rounded-2xl hover:bg-[var(--ink-muted)] transition-all hover:-translate-y-0.5"
              >
                Start Organizing Free
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </a>
              <a
                href="#features"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-[var(--ink)] bg-white rounded-2xl hover:bg-[var(--cream)] transition-all shadow-sm ring-1 ring-[var(--cream)] hover:-translate-y-0.5"
              >
                See How It Works
              </a>
            </div>

            {/* LMS badge */}
            <p className="hero-animate hero-animate-delay-5 mt-8 text-sm text-[var(--ink-light)] flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                />
              </svg>
              Syncs with your school's LMS
            </p>
          </div>
        </div>
      </section>

      {/* Dashboard Preview - overlapping into hero */}
      <section className="pb-16 sm:pb-20 -mt-4">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="dashboard-preview bg-white rounded-3xl shadow-2xl overflow-hidden ring-1 ring-[var(--cream)] lg:transform lg:rotate-[-1deg] hover:rotate-0 transition-transform duration-500">
            {/* Fake browser chrome */}
            <div className="bg-[var(--paper-warm)] px-4 py-3 border-b border-[var(--cream)] flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              <div className="flex-1 flex justify-center">
                <div className="bg-[var(--cream)] rounded-lg px-4 py-1.5 text-sm text-[var(--ink-light)] flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  heartily.app
                </div>
              </div>
            </div>

            {/* Dashboard content */}
            <div className="p-6 sm:p-8 bg-[var(--paper)]">
              {/* Stats bar preview */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                <DemoStatCard
                  label="Overdue"
                  value={demoStats.overdue}
                  gradient="from-red-500 to-rose-600"
                  iconColor="text-red-500"
                  urgent={true}
                  icon={
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  }
                />
                <DemoStatCard
                  label="Today"
                  value={demoStats.today}
                  gradient="from-amber-500 to-orange-500"
                  iconColor="text-amber-500"
                  icon={
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  }
                />
                <DemoStatCard
                  label="This Week"
                  value={demoStats.thisWeek}
                  gradient="from-blue-500 to-indigo-500"
                  iconColor="text-blue-500"
                  icon={
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  }
                />
                <DemoStatCard
                  label="Submitted"
                  value={demoStats.submitted}
                  gradient="from-emerald-500 to-green-600"
                  iconColor="text-emerald-500"
                  icon={
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  }
                />
              </div>

              {/* Assignment cards preview */}
              <div className="space-y-3">
                {demoAssignments.map((assignment) => (
                  <DemoAssignmentCard key={assignment.id} assignment={assignment} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[var(--paper-warm)]" aria-labelledby="ai-feature-title">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-base font-semibold text-[var(--gold)] mb-4">New · Connect your AI assistant</p>
            <h2 id="ai-feature-title" className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-balance max-w-[35ch]">Your assignments.<br />Your favorite assistant.</h2>
            <p className="text-lg text-[var(--ink-muted)] mt-5 max-w-[48ch] text-pretty">Plan your week, review deadlines and make sense of grades with Claude, ChatGPT, Grok or another compatible assistant. Heartily gives it the school data you choose to share.</p>
            <p className="mt-6 font-medium"><Link to="/ai-assistants" className="text-[var(--ink)] underline underline-offset-4 hover:text-[var(--gold)]">See what’s possible and how to connect →</Link></p>
          </div>
          <div className="rounded-2xl bg-white p-6 sm:p-8 ring-1 ring-black/5">
            <p className="text-base sm:text-sm text-[var(--ink-light)] mb-5">Try asking your assistant</p>
            <blockquote className="font-display text-2xl sm:text-3xl text-[var(--ink)]">“What’s due this week, and where should I start?”</blockquote>
            <p className="mt-6 pt-6 border-t border-black/5 text-base text-[var(--ink-muted)]">Read-only access. Students share their own work; parents share only currently linked students.</p>
            <p className="mt-4 text-base sm:text-sm text-[var(--ink-light)]">Custom connector setup required. Availability depends on your assistant and plan.</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[var(--ink)] mb-4">
              Everything you need to stay on top
            </h2>
            <p className="text-lg text-[var(--ink-muted)] max-w-2xl mx-auto">
              Heartily pulls all your assignments from your LMS and organizes them so you always know what needs your
              attention first.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="p-6 rounded-2xl bg-[var(--paper)] ring-1 ring-[var(--cream)] md:col-span-2 lg:col-span-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                <div className="min-w-0">
                  <h3 className="font-display text-xl font-semibold text-[var(--ink)] mb-2">AI Assistant Connection (MCP)</h3>
                  <p className="text-base text-[var(--ink-muted)] max-w-[75ch]">
                    Connect Claude, ChatGPT, Grok and compatible AI assistants to your courses, assignments and grades.
                    Plan your week and check deadlines with secure OAuth sign-in and read-only access to your own
                    data—or your currently linked students’ data as a parent.
                  </p>
                </div>
                <p className="shrink-0 font-medium">
                  <Link to="/ai-assistants" className="inline-flex min-h-11 items-center text-[var(--ink)] underline underline-offset-4 hover:text-[var(--gold)]">
                    Learn how to connect →
                  </Link>
                </p>
              </div>
            </div>

            {/* Feature 1 */}
            <div className="p-6 rounded-2xl bg-[var(--paper)] ring-1 ring-[var(--cream)]">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </div>
              <h3 className="font-display text-xl font-semibold text-[var(--ink)] mb-2">LMS Sync</h3>
              <p className="text-[var(--ink-muted)] leading-relaxed">
                Connect your LMS and watch all your courses, assignments, and submissions sync automatically.
                One click to stay up to date.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="p-6 rounded-2xl bg-[var(--paper)] ring-1 ring-[var(--cream)]">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="font-display text-xl font-semibold text-[var(--ink)] mb-2">Smart Organization</h3>
              <p className="text-[var(--ink-muted)] leading-relaxed">
                Assignments automatically grouped by urgency: overdue, due today, due tomorrow, this week, and coming
                up. Always know what's most important.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="p-6 rounded-2xl bg-[var(--paper)] ring-1 ring-[var(--cream)]">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                  />
                </svg>
              </div>
              <h3 className="font-display text-xl font-semibold text-[var(--ink)] mb-2">Grade Tracking</h3>
              <p className="text-[var(--ink-muted)] leading-relaxed">
                See your grades as they come in with visual progress indicators. Know exactly where you stand in each
                course at a glance.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="p-6 rounded-2xl bg-[var(--paper)] ring-1 ring-[var(--cream)]">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="font-display text-xl font-semibold text-[var(--ink)] mb-2">Status Alerts</h3>
              <p className="text-[var(--ink-muted)] leading-relaxed">
                Clear visual indicators for missing, late, submitted, graded, and excused assignments. Never be
                surprised by a zero again.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="p-6 rounded-2xl bg-[var(--paper)] ring-1 ring-[var(--cream)]">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h3 className="font-display text-xl font-semibold text-[var(--ink)] mb-2">Course Filtering</h3>
              <p className="text-[var(--ink-muted)] leading-relaxed">
                Filter by course to focus on one class at a time. Hide courses you don't need to see. Filter by semester
                to see current or past work.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="p-6 rounded-2xl bg-[var(--paper)] ring-1 ring-[var(--cream)]">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h3 className="font-display text-xl font-semibold text-[var(--ink)]">Parent Dashboard</h3>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-50 text-cyan-800 ring-1 ring-cyan-200">
                  Optional
                </span>
              </div>
              <p className="text-[var(--ink-muted)] leading-relaxed">
                Only if a student invites them. Invited parents can see assignments, grades, and what needs attention.
                Nothing is shared unless the student sends an invite.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Section */}
      <section className="py-16 sm:py-24 bg-[var(--paper)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium mb-6 ring-1 ring-blue-200">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                Now on iOS
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[var(--ink)] mb-6">
                Your assignments, right in your pocket
              </h2>
              <p className="text-lg text-[var(--ink-muted)] mb-8 leading-relaxed">
                Get the Heartily iOS app and never miss a deadline. Push notifications remind you when assignments
                are due soon so nothing slips through the cracks. All the same features, optimized for your phone.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                  <span className="text-[var(--ink-muted)]">Push notifications for upcoming due dates</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <span className="text-[var(--ink-muted)]">Sync your LMS on the go with one tap</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="text-[var(--ink-muted)]">Check assignments anywhere, even between classes</span>
                </li>
              </ul>
              <a
                href="https://apps.apple.com/us/app/heartily/id6758560233"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-6 py-3 bg-black text-white rounded-xl hover:bg-gray-800 transition-all shadow-sm hover:-translate-y-0.5"
              >
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                <div className="text-left">
                  <div className="text-[10px] leading-tight opacity-80">Download on the</div>
                  <div className="text-lg font-semibold leading-tight">App Store</div>
                </div>
              </a>
            </div>

            {/* Phone mockup */}
            <div className="flex justify-center">
              <div className="relative w-[280px]">
                {/* Phone frame */}
                <div className="bg-gray-900 rounded-[3rem] p-3 shadow-2xl ring-1 ring-gray-800">
                  {/* Screen */}
                  <div className="bg-[var(--paper)] rounded-[2.25rem] overflow-hidden">
                    {/* Status bar */}
                    <div className="h-12 bg-white flex items-end justify-center pb-1">
                      <div className="w-24 h-5 bg-gray-900 rounded-full" />
                    </div>
                    {/* App content preview */}
                    <div className="p-4 space-y-3">
                      {/* Mini stats */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="p-2.5 bg-red-50 rounded-xl text-center">
                          <div className="font-display text-xl font-bold text-red-600">2</div>
                          <div className="text-[10px] text-red-600 font-medium">Overdue</div>
                        </div>
                        <div className="p-2.5 bg-amber-50 rounded-xl text-center">
                          <div className="font-display text-xl font-bold text-amber-600">3</div>
                          <div className="text-[10px] text-amber-600 font-medium">Today</div>
                        </div>
                      </div>
                      {/* Mini assignment cards */}
                      <div className="space-y-2">
                        <div className="p-3 bg-white rounded-xl shadow-sm ring-1 ring-[var(--cream)]">
                          <div className="flex items-center gap-2">
                            <div className="w-1 h-8 rounded-full bg-violet-500 opacity-60" />
                            <div className="flex-1">
                              <div className="text-xs font-medium text-[var(--ink)] truncate">Chapter 8 Quiz</div>
                              <div className="text-[10px] text-[var(--ink-light)]">Biology 101</div>
                            </div>
                            <span className="text-[10px] text-amber-600 font-medium">3h</span>
                          </div>
                        </div>
                        <div className="p-3 bg-white rounded-xl shadow-sm ring-1 ring-[var(--cream)]">
                          <div className="flex items-center gap-2">
                            <div className="w-1 h-8 rounded-full bg-cyan-500 opacity-60" />
                            <div className="flex-1">
                              <div className="text-xs font-medium text-[var(--ink)] truncate">Research Paper</div>
                              <div className="text-[10px] text-[var(--ink-light)]">English Comp</div>
                            </div>
                            <span className="text-[10px] text-blue-600 font-medium">1d</span>
                          </div>
                        </div>
                        <div className="p-3 bg-white rounded-xl shadow-sm ring-1 ring-[var(--cream)]">
                          <div className="flex items-center gap-2">
                            <div className="w-1 h-8 rounded-full bg-fuchsia-500 opacity-60" />
                            <div className="flex-1">
                              <div className="text-xs font-medium text-[var(--ink-light)] line-through truncate">Problem Set #6</div>
                              <div className="text-[10px] text-emerald-600">94% - Graded</div>
                            </div>
                            <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Notification preview */}
                      <div className="p-3 bg-blue-50 rounded-xl ring-1 ring-blue-100">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-medium text-blue-800 truncate">Quiz due in 3 hours!</div>
                            <div className="text-[10px] text-blue-600 truncate">Biology 101</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Bottom padding for home indicator */}
                    <div className="h-6" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parent Dashboard Preview */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-white to-[var(--paper)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-cyan-50 text-cyan-700 rounded-full text-sm font-medium mb-6 ring-1 ring-cyan-200">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                Optional · Student invite only
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[var(--ink)] mb-6">
                Stay connected to your student's progress
              </h2>
              <p className="text-lg text-[var(--ink-muted)] mb-8 leading-relaxed">
                Parent access is optional. Students choose whether to invite anyone, and parents cannot link themselves.
                If invited, parents get their own view of assignments that need attention, recent grades, and what's
                coming up — without constant check-ins.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-[var(--ink-muted)]">Nothing is shared until the student sends an invite</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-[var(--ink-muted)]">See overdue and upcoming assignments at a glance</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-[var(--ink-muted)]">Track grades as they're posted</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-[var(--ink-muted)]">Monitor multiple students from one account</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-[var(--ink-muted)]">Request a fresh sync from the LMS anytime</span>
                </li>
              </ul>
            </div>

            {/* Parent dashboard preview */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden ring-1 ring-[var(--cream)]">
              <div className="p-6 bg-[var(--paper)]">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                    <span className="text-white font-semibold text-lg">JS</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[var(--ink)]">James Smith</h3>
                    <p className="text-sm text-[var(--ink-light)]">Spring 2026</p>
                  </div>
                  <div className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 text-sm font-medium">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    2 need attention
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-red-50 rounded-xl text-center">
                    <div className="font-display text-2xl font-bold text-red-600">2</div>
                    <div className="text-xs text-red-600 font-medium">Overdue</div>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-xl text-center">
                    <div className="font-display text-2xl font-bold text-amber-600">3</div>
                    <div className="text-xs text-amber-600 font-medium">Due Today</div>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-xl text-center">
                    <div className="font-display text-2xl font-bold text-emerald-600">5</div>
                    <div className="text-xs text-emerald-600 font-medium">Graded</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Story Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-[var(--cream)]/50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[var(--ink)] mb-4">Why "Heartily"?</h2>
          </div>

          <div className="bg-white rounded-3xl shadow-lg p-8 sm:p-12 ring-1 ring-[var(--cream)]">
            <blockquote className="text-center mb-8">
              <p className="font-display text-2xl sm:text-3xl text-[var(--ink)] italic leading-relaxed mb-4">
                "Whatever you do, work heartily, as for the Lord and not for men."
              </p>
              <cite className="text-[var(--ink-muted)] not-italic">— Colossians 3:23</cite>
            </blockquote>

            <div className="section-divider" />

            <div className="prose prose-lg max-w-none text-[var(--ink-muted)]">
              <p className="leading-relaxed mb-6">
                Heartily was created by a Clemson University alumnus and current Clemson dad. When his son started
                college, he noticed something familiar: the struggle to keep track of what was due and when across
                multiple classes.
              </p>

              <p className="leading-relaxed mb-6">
                With assignments scattered across different courses, confusing due date formats, and no simple way to
                see what needed attention first, it was easy to miss deadlines—not from lack of effort, but from lack
                of clarity.
              </p>

              <p className="leading-relaxed mb-6">
                Heartily was built to help his son succeed academically by bringing all his assignments into one
                clear, organized view. The goal wasn't just to track assignments, but to remove the friction that gets
                in the way of doing excellent work.
              </p>

              <p className="leading-relaxed">
                The name comes from that verse in Colossians, a reminder that how we approach our work matters. Whether
                it's a quiz, a research paper, or a problem set, students deserve tools that help them give their best
                effort to every assignment.
              </p>
            </div>

            <div className="mt-8 flex items-center justify-center gap-2 text-[var(--gold)]">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
              </svg>
              <span className="font-medium text-[var(--ink-muted)]">Go Tigers!</span>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-[var(--ink)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium mb-8">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Completely Free for Fall 2026
          </div>

          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to take control of your assignments?
          </h2>

          <p className="text-lg sm:text-xl text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">
            Connect your LMS and see all your assignments organized in one place. No credit card required.
            No catch. Just a tool built to help students succeed.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/users/sign_up"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold bg-white text-[var(--ink)] rounded-2xl hover:bg-[var(--cream)] transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            >
              Create Your Free Account
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </a>
            <a
              href="https://apps.apple.com/us/app/heartily/id6758560233"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 text-base font-semibold bg-white/10 text-white rounded-2xl hover:bg-white/20 transition-all shadow-lg ring-1 ring-white/20 hover:-translate-y-0.5"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              Download iOS App
            </a>
          </div>

          <p className="mt-8 text-sm text-white/50">
            Works with any school that uses Canvas LMS and allows for third party Access Tokens
            <br />
            Also works with any LMS that provides an iCal feed for assignments and due dates;
            grades and completion status do not sync.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[var(--paper)] border-t border-[var(--cream)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-6">
            <div className="flex items-center">
              <img
                src="/images/heartilylogo.png"
                alt="Heartily"
                className="h-7"
              />
            </div>

            <p className="text-sm text-[var(--ink-light)]">
              Made with care for students and parents.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              <a href="/users/sign_in" className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]">
                Sign In
              </a>
              <a href="/users/sign_up" className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]">
                Sign Up
              </a>
              <a
                href="https://apps.apple.com/us/app/heartily/id6758560233"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]"
              >
                iOS App
              </a>
              <Link to="/ai-assistants" className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]">AI assistants</Link>
              <Link to="/legal/terms" className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]">
                Terms of Service
              </Link>
              <Link to="/legal/acceptable-use" className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]">
                Acceptable Use
              </Link>
              <Link to="/legal/privacy" className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]">
                Privacy Policy
              </Link>
            </div>

            <p className="text-xs text-[var(--ink-light)]">
              &copy; {new Date().getFullYear()} Hosting Playground Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default MarketingPage
